import { ApiLearnerOutcome } from "./models";

/**
 * Load all learner outcomes from either a remote URL or static JSON data.
 *
 * This data is pre-transformed and includes:
 * - All performance level IDs (e.g., "FL.ID.1.a.1")
 * - All parent IDs on activities (learnerOutcomeId, competencyId, etc.)
 * - Full hierarchy of learner outcomes, outcome areas, competencies, sub-competencies, and activities
 *
 * Data source priority:
 * 1. If COMPETENCY_DATA_URL env variable is set, fetch from that URL
 * 2. If URL fetch fails, fall back to static JSON file
 * 3. Otherwise, use static JSON file: public/competencies-data.json
 */
export const getAllLearnerOutcomes = async (): Promise<ApiLearnerOutcome[]> => {
  console.log("=".repeat(80));
  console.log("🚀 COMPETENCY DATA LOADER - STARTING");
  console.log("=".repeat(80));

  const dataUrl = process.env.COMPETENCY_DATA_URL;
  console.log(
    `📋 Environment Variable COMPETENCY_DATA_URL: ${
      dataUrl ? `SET (${dataUrl})` : "NOT SET"
    }`,
  );

  let competenciesData: ApiLearnerOutcome[];
  let dataSource = "static JSON file";
  const startTime = Date.now();

  // Try to fetch from URL if configured
  if (dataUrl) {
    console.log(`\n🌐 ATTEMPTING TO LOAD FROM REMOTE URL`);
    console.log(`   URL: ${dataUrl}`);

    try {
      console.log(`   ⏳ Fetching data...`);
      const response = await fetch(dataUrl);
      console.log(
        `   📡 Response Status: ${response.status} ${response.statusText}`,
      );
      console.log(
        `   📝 Content-Type: ${response.headers.get("content-type")}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log(`   🔄 Parsing JSON response...`);
      competenciesData = await response.json();
      dataSource = `REMOTE URL: ${dataUrl}`;
      console.log(`   ✅ SUCCESS - Data loaded from remote URL`);
    } catch (error) {
      console.log(`\n⚠️  ERROR LOADING FROM URL`);
      console.log(
        `   Error: ${error instanceof Error ? error.message : String(error)}`,
      );
      console.log(`   ${error instanceof Error ? error.stack : ""}`);
      console.log(`\n📁 FALLING BACK TO STATIC JSON FILE`);

      // Fall back to static file
      const staticData = await import("@/../public/competencies-data.json");
      competenciesData = staticData.default as ApiLearnerOutcome[];
      dataSource = "STATIC FILE (fallback after URL error)";
    }
  } else {
    // No URL configured, use static file
    console.log(`\n📁 LOADING FROM STATIC JSON FILE (no URL configured)`);
    const staticData = await import("@/../public/competencies-data.json");
    competenciesData = staticData.default as ApiLearnerOutcome[];
    dataSource = "STATIC FILE";
  }

  // Validation
  if (!Array.isArray(competenciesData)) {
    throw new Error("Invalid competencies data format: expected array");
  }

  const loadTime = Date.now() - startTime;
  const firstOutcome = competenciesData[0];

  console.log("\n" + "=".repeat(80));
  console.log("✅ COMPETENCY DATA LOADER - COMPLETE");
  console.log("=".repeat(80));
  console.log(`📊 Total Learner Outcomes: ${competenciesData.length}`);
  console.log(`🎯 Data Source: ${dataSource}`);
  console.log(`⏱️  Load Time: ${loadTime}ms`);
  console.log(
    `📝 First Outcome: ${firstOutcome?.name || "N/A"} (ID: ${
      firstOutcome?.id || "N/A"
    })`,
  );
  console.log("=".repeat(80));

  return competenciesData;
};
